import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { passwordRequirements } from "@/utils/validation";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

const ForcePasswordChangeDialog = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!user) {
        setOpen(false);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("must_change_password")
        .eq("id", user.id)
        .maybeSingle();
      if (data?.must_change_password) setOpen(true);
      else setOpen(false);
    };
    check();
  }, [user]);

  const allRequirementsMet = passwordRequirements.every((r) => r.regex.test(password));
  const passwordsMatch = password.length > 0 && password === confirm;

  const getFriendlyError = (msg: string): string => {
    const m = msg.toLowerCase();
    if (m.includes("weak") || m.includes("pwned") || m.includes("known to be")) {
      return "Ce mot de passe figure dans une liste de mots de passe compromis. Veuillez en choisir un autre, plus unique (évitez les mots courants, suites de chiffres, ou mots de passe déjà utilisés ailleurs).";
    }
    if (m.includes("same") || m.includes("different from the old")) {
      return "Le nouveau mot de passe doit être différent de l'ancien.";
    }
    return msg;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRequirementsMet || !passwordsMatch || !user) return;
    setLoading(true);
    try {
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) throw pwError;

      const { error: profileError } = await supabase
        .from("profiles")
        .update({ must_change_password: false })
        .eq("id", user.id);
      if (profileError) throw profileError;

      toast({ title: "Mot de passe modifié", description: "Votre nouveau mot de passe est actif." });
      setOpen(false);
      setPassword("");
      setConfirm("");
    } catch (err: any) {
      toast({
        title: "Mot de passe refusé",
        description: getFriendlyError(err.message || "Erreur inconnue"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <DialogPrimitive.Root open={open} modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%]",
            "gap-4 border bg-background p-6 shadow-lg rounded-lg max-h-[90vh] overflow-auto"
          )}
        >
          <div>
            <DialogPrimitive.Title className="text-lg font-semibold">
              Changement de mot de passe requis
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground mt-1">
              Pour votre sécurité, vous devez définir un nouveau mot de passe avant de continuer. Vous ne pouvez pas fermer cette fenêtre.
            </DialogPrimitive.Description>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <ul className="text-sm space-y-1">
              {passwordRequirements.map((req) => {
                const ok = req.regex.test(password);
                return (
                  <li key={req.label} className={`flex items-center gap-2 ${ok ? "text-green-600" : "text-muted-foreground"}`}>
                    {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {req.label}
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted-foreground">
              Astuce : évitez les mots de passe courants ou déjà utilisés ailleurs (ex: "Password1!", "Azerty123!"). Privilégiez une phrase de passe unique.
            </p>
            <div>
              <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
              {confirm.length > 0 && !passwordsMatch && (
                <p className="text-sm text-destructive mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>
            <Button type="submit" disabled={loading || !allRequirementsMet || !passwordsMatch} className="w-full">
              {loading ? "Enregistrement..." : "Définir le nouveau mot de passe"}
            </Button>
            <Button type="button" variant="ghost" onClick={handleSignOut} className="w-full">
              Se déconnecter
            </Button>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default ForcePasswordChangeDialog;
